import React, { useEffect, useState, Fragment } from "react";
import { Helmet } from "react-helmet";
import { Dialog, Transition, Menu, Popover } from '@headlessui/react'
import { SparklesIcon } from "@heroicons/react/outline";
import {
  Switch,
  Route,
  Link,
  Redirect
} from "react-router-dom";
import { CSVLink } from "react-csv";

import Drawer from "../common/drawer";
import ErrorBar from "../common/errorBar";
import PropsRoute from '../common/propsroute';
import ErrorBoundary from "../common/errorboundary";
import NavBar from "../common/navbar";

import SavedConversationsAll from "../conversations/savedconversationsall";
import SavedConversationsAnalyze from "../conversations/savedconversationsanalyze";
import SavedConversationsSimilar from "../conversations/savedconversationssimilar";

import { savedSearchesService } from '../../services/savedSearches';
import { redditService } from '../../services/reddit';
import { slugify } from '../../utils/text';
import { savedSearchOperations } from "../../redux/saved"

import { trackEvent } from '../../utils/tracking';
import { contains } from '../../utils/numbers';
import { getConversationCsvDict, applyFrontendFilters } from '../../utils/reddit';
import { toQueryString } from '../../utils/urls';


// right sidebar for a saved search result
// baseUrl is the url that this component stems from (it's used in multiple places)
const AudienceSuggestionBookmarkDrawer = ({
  match,
  history,
  location,
  bookmarks,
  collection,
  closeModal,// TODO: add
  currentUser,
  refetchAll, // TODO: add
}) => {
  const { savedSearchHash } = match.params; 
  const [savedSearch, setSavedSearch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [downloadableResults, setDownloadableResults] = useState([]);  // set from child component
  const isProSubscriber = currentUser && currentUser.subscription && (currentUser.subscription.key === "pro");
  const isEffectiveProSubscriber = isProSubscriber || currentUser.trial.plan === 'pro';

  const [results, setResults] = useState([]); // combined submissions & comments
  const [fetching, setFetching] = useState(false); // just for the saved conversation & reddit IDs
  const [fetchingResults, setFetchingResults] = useState(false); // for actual conversation
  
  const [aggregates, setAggregates] = useState(null);
  const [fetchingAggregates, setFetchingAggregates] = useState(false);
  const [fetchingExtra, setFetchingExtra] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [frontendFilters, setFrontendFilters] = useState({});  // {key: [value1, value2]}

  useEffect(() => {
    fetchSavedSearch()
  }, [
    savedSearchHash,
  ]); // Run when saved search ID changes

  // effects
  useEffect(() => {
    if (savedSearch){
      fetchConversations();
    }
  }, [savedSearch]); // Go fetch all conversations when we get here

  // actions
  const fetchSavedSearch = () => {
    setLoading(true)
    setError(null)
    savedSearchesService.getSavedSearch(match.params.savedSearchHash, (results) => {
      if (results && results.data){
        setSavedSearch(results.data)
        setLoading(false)
      }
    }, (response, error) => {
      setError(error)
      setLoading(false)
    })
  }

  const fetchConversations = () => {
    setFetching(true);
    setError(null);
    savedSearchesService.getManualConversations(savedSearch.hash, (response) => {
      const submissionIds = response.data.filter(r => r.conversation_type === "RSU").map(r => r.platform_id)
      const commentIds = response.data.filter(r => r.conversation_type === "RCO").map(r => r.platform_id);
      fetchCombinedResults([...submissionIds, ...commentIds])
      setFetching(false)
      
    }, (error, response) => {
      setFetching(false);
        if (error.response && error.response.status === 500){
          setError("Error fetching conversations.")
        } else {
          setError(response || "Error fetching conversations.")
        }
    })
  }

  const fetchCombinedResults = (ids) => {
    // fetches all results based on IDs
    // WATCH OUT! If there are clashes between comment & submission IDs, we might have some invalid convos coming up
    // this method assumes comment & submission IDs on reddit don't overlap, but they totally could
    // in that case, we might need to break out this call into 2 (specifying comments & submissions)

    // track the search
    const trackParams = {
      "platform": "Reddit",
      "saved_search": "true",
      "saved_search_manual": "true",
      "type": "reddit-all",
    }
    trackEvent("PerformConversationSearch", trackParams);

    setFetchingResults(true);
    setError(false);
    redditService.getConversations({"ids": ids, "type": "reddit-all", "backend": "praw"},
      (response) => {
        const bookmarkResults = response.data.results.filter(r => collection.subreddit_names.includes(r.subreddit_name.replace('r/', '')));
        setResults(bookmarkResults);
        fetchExtras(bookmarkResults);
        fetchAggregates(ids);
        setFetchingResults(false);
      }, (error, response) => {
        setFetching(false);
        if (error.response && error.response.status === 500){
          setError("Error fetching results from Reddit.")
        } else {
          setError(response || "Error fetching results from Reddit.")
        }
      });
  }

  // gets extra data on this query, updates results
  const fetchExtras = (currentResults) => {
    if (!currentResults || !currentResults.length) return; // don't call extras if there's nothing to do
    const ids = currentResults.map(r => r.id);
    const allFilters = {"ids": ids, "type": "reddit-all", "backend": "praw"};
    setFetchingExtra(true);
    redditService.getConversationExtras(allFilters,
      (response) => {
        // update responses based on the IDs
        const updates = response.data;
        var newResults = currentResults.map((r) => {
          if (updates[r.id]){
            r = Object.assign(r, updates[r.id])
          }
          return r
        });
        setResults(newResults);
        setFetchingExtra(false);
      }, (error, response) => {
        setFetchingExtra(false);
        console.error("error fetching extras", response)
      });
  }

  // gets aggregate data on this query
  const fetchAggregates = (ids) => {
    if (!ids || !ids.length) return; // don't call aggregates if there's nothing to do
    const allFilters = {"ids": ids, "type": "reddit-all", "backend": "praw"};
    setFetchingAggregates(true);
    redditService.getConversationAggregates(allFilters,
      (response) => {
        setAggregates(response.data);
        setFetchingAggregates(false);
      }, (error, response) => {
        setFetchingAggregates(false);
        console.error("error fetching aggregates", response)
      });
  }

  // state helpers
  const baseUrl = `/audience/${collection.hash}/themes/bookmark/${savedSearchHash}/`;

  // results & filtered results
  var filteredResults = applyFrontendFilters(results, frontendFilters);

  // counts & tabs
  const countResults = results && results.length;
  const countFilteredResults = filteredResults.length;
  const tabs = [
    { name: `${countResults ? (countFilteredResults !== countResults ? `${countFilteredResults} / ${countResults}` : countResults) : ''} Saved`, link: `${baseUrl}all/`},
    // { name: 'Filters', link: `${baseUrl}aggregate/`, disabled: !results || !results.length},
    { name: 'Patterns', link: `${baseUrl}patterns/`},
    ...(savedSearch && savedSearch.search_type === 'MA' ? [{ name: 'Similar', link: `${baseUrl}similar/`, 'icon': SparklesIcon}] : []),
  ]

  return (
    <Drawer close={closeModal}>
      <ErrorBoundary>
        <div className="flex flex-col h-full">
          <Helmet><title>Conversations | Saved</title></Helmet>

          <div className="p-4">
            <div className="flex items-start justify-between">
              <div className="text-lg font-medium flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mr-2" viewBox="0 0 24 24"
                  stroke={savedSearch ? savedSearch.color : '#FAFAFA'} fill={`${savedSearch ? savedSearch.color : '#FAFAFA'}75`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                {savedSearch ? (
                  <div className="">
                    <div>{savedSearch.name}</div>
                    <div className="text-xs opacity-50">{savedSearch.count_conversations} conversation{savedSearch.count_conversations === 1 ? '' : 's'}</div>
                  </div>
                ) : (
                  <div className="h-11"></div>
                )}
                
              </div>
              <div className="ml-3 h-7 flex items-center space-x-2">

                <button
                  className="rounded-md text-gray-400 hover:text-gray-300 focus:outline-none flex items-center space-x-1"
                  onClick={closeModal}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="hidden sm:inline-block">Close</span>
                </button>

              </div>
            </div>
          </div>

          <div className="px-4 flex items-center flex-wrap border-b border-solid border-gray-800">
            <NavBar tabs={tabs} />
            <div className="ml-auto"></div>
          </div>
          
          {savedSearch ? (
            <div id="search-results-content" className="flex-grow overflow-hidden flex flex-col">
              <Switch>
                <Redirect exact path={`${baseUrl}`} to={`${baseUrl}all/`} />
                <PropsRoute path={`${baseUrl}all/`} component={SavedConversationsAll}
                  savedSearch={savedSearch} currentUser={currentUser} baseUrl={baseUrl}
                  results={filteredResults} fetching={fetching || fetchingResults}
                  fetchConversations={fetchConversations}
                />
                <PropsRoute path={`${baseUrl}patterns/`} component={SavedConversationsAnalyze}
                  savedSearch={savedSearch} currentUser={currentUser} baseUrl={baseUrl}
                  results={filteredResults} fetching={fetching || fetchingResults}
                  // fetchConversations={fetchConversations}
                />
                <PropsRoute path={`${baseUrl}similar/`} component={SavedConversationsSimilar}
                  savedSearch={savedSearch} currentUser={currentUser} baseUrl={baseUrl}
                  collectionHash={collection.hash}
                />
              </Switch>
            </div>
          ) : ''}
          
          {error ? (
            <ErrorBar error={error} setError={setError} onRetry={fetchConversations}/>
          ) : ''}    
        </div>
      </ErrorBoundary>  
    </Drawer>
  );
}

export default AudienceSuggestionBookmarkDrawer;

